import { useState } from "react";
import {
  ShieldCheck, Lock, Smartphone, KeyRound, DoorClosed, ClipboardCheck, Copy, Check,
  CheckCircle2, Loader2, CalendarDays, Clock, MessageCircleQuestion,
} from "lucide-react";
import { api, formatError, waLink, PHONE_DISPLAY } from "@/lib/api";
import { Eyebrow, FadeIn } from "@/components/Reveal";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";

const STEPS = [
  { icon: CalendarDays, title: "Reserva tu visita", body: "Elige día y hora en el formulario de abajo. Recibirás la confirmación con la hora exacta de acceso." },
  { icon: MapPinIcon, title: "Acude al inmueble", body: "El día de tu cita, acércate a la puerta de la vivienda a la hora reservada. No tienes que esperar a nadie." },
  { icon: Smartphone, title: "Abre desde tu móvil", body: "A la hora de tu visita activamos el acceso: abres la cerradura electrónica de forma remota y segura con tu propio dispositivo." },
  { icon: KeyRound, title: "Visita a tu ritmo", body: "Entra y recorre toda la vivienda con total libertad, sin prisas ni presión comercial. Tómate el tiempo que necesites." },
  { icon: DoorClosed, title: "Al salir, se cierra sola", body: "Cuando termines, simplemente sal. La puerta se cierra y la alarma se arma automáticamente." },
  { icon: ClipboardCheck, title: "Todo seguro y monitorizado", body: "El acceso queda registrado y el inmueble está vigilado en todo momento. Máxima tranquilidad." },
];

const SECURITY = [
  { icon: ShieldCheck, title: "Alarma de Securitas Direct", body: "Vivienda protegida y vigilada las 24 horas durante y después de tu visita." },
  { icon: Lock, title: "Cerradura electrónica remota", body: "Abrimos y cerramos la puerta a distancia; solo se activa en tu franja horaria." },
  { icon: ClipboardCheck, title: "Acceso registrado", body: "Cada entrada y salida queda registrada para tu seguridad y la del propietario." },
  { icon: DoorClosed, title: "Cierre y armado automático", body: "Al salir no tienes que hacer nada: la puerta se cierra y la alarma de Securitas Direct se arma sola." },
];

const FAQ = [
  { q: "¿Necesito descargar alguna app?", a: "Sí, solo necesitas descargar una app muy sencilla, rápida y totalmente gratuita (sin ningún coste) para abrir la cerradura electrónica durante tu visita." },
  { q: "¿Y si llego tarde o no puedo ir?", a: "El acceso se habilita en tu franja horaria. Si necesitas cambiar el día o la hora, avísanos por WhatsApp y reprogramamos tu visita sin problema." },
  { q: "¿Es seguro entrar solo/a?", a: "Totalmente. Cada inmueble cuenta con alarma de Securitas Direct y cerradura electrónica. El acceso está monitorizado y registrado, y al salir todo se cierra y se arma automáticamente." },
  { q: "¿Puedo ir con mi familia o pareja?", a: "Claro. Puedes acudir acompañado/a a tu visita. Recorréis la vivienda con total libertad durante el tiempo de vuestra cita." },
];

function MapPinIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

const inputCls =
  "w-full rounded-lg border border-gold/20 bg-ink px-4 py-3 text-ivory placeholder:text-stone2/60 outline-none transition-colors focus:border-gold";
const labelCls = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.16em] text-gold-subtle";

const Visitas = () => {
  const [f, setF] = useState({ name: "", phone: "", property_ref: "", date: "", time: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [waUrl, setWaUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/visitas`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setErr("");
    try {
      const { data } = await api.post("/visit-booking", f);
      setWaUrl(data.wa_url);
      setSent(true);
      window.open(data.wa_url, "_blank");
    } catch (error) {
      setErr(formatError(error.response?.data?.detail) || "No se pudo enviar. Escríbenos directamente por WhatsApp.");
    }
    setSending(false);
  };

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-gold/10 bg-ink2/40">
        <div className="mx-auto max-w-5xl px-6 pb-20 pt-32 text-center md:pt-44">
          <FadeIn>
            <Eyebrow className="justify-center">Visitas sin presencia</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl uppercase tracking-wide text-ivory md:text-6xl">
              Visitas <span className="text-gold-metallic">100% autónomas</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl leading-relaxed text-stone2">
              Visita las viviendas de Tecnohogar tú solo/a, sin comerciales. Inmuebles con alarma de Securitas
              Direct y cerradura electrónica remota: accedes con tu móvil a la hora reservada y al salir se cierra y
              arma la alarma automáticamente.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="#reservar" data-testid="visitas-cta-reservar" className="btn-gold-metallic rounded-full px-8 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-ink">
                Reservar mi visita
              </a>
              <a
                href={waLink("Hola, tengo una duda sobre las visitas sin presencia de Tecnohogar")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visitas-cta-whatsapp"
                className="inline-flex items-center gap-2 rounded-full border border-wa/40 bg-wa/10 px-8 py-3.5 text-sm font-bold uppercase tracking-[0.16em] text-wa transition-colors hover:bg-wa hover:text-ink"
              >
                <WhatsAppIcon className="h-4 w-4" /> Dudas por WhatsApp
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PASOS */}
      <section className="mx-auto max-w-7xl px-6 py-20 md:px-8 md:py-28">
        <Eyebrow>Cómo funciona</Eyebrow>
        <h2 className="mt-4 font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
          Seis pasos y <span className="text-gold-metallic">ni uno más</span>
        </h2>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <FadeIn key={s.title} delay={(i % 3) * 0.08}>
              <div className="h-full rounded-2xl border border-gold/20 bg-ink2/80 p-6 transition-colors duration-500 hover:border-gold/50">
                <div className="flex items-center justify-between">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full border border-gold/30 text-gold">
                    <s.icon size={19} />
                  </span>
                  <span className="font-serif text-4xl text-gold/30">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-4 font-serif text-xl text-ivory">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-stone2">{s.body}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* SEGURIDAD */}
      <section className="border-y border-gold/10 bg-ink2/40">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-8 md:py-28">
          <Eyebrow>Seguridad</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
            Tu visita, <span className="text-gold-metallic">bajo control</span>
          </h2>
          <p className="mt-3 flex items-center gap-2 text-sm text-gold" data-testid="visitas-securitas-badge">
            <ShieldCheck size={15} /> Protegido por Securitas Direct
          </p>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SECURITY.map((s, i) => (
              <FadeIn key={s.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-gold/20 bg-ink/70 p-6">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold/10 text-gold">
                    <s.icon size={19} />
                  </span>
                  <h3 className="mt-4 font-serif text-lg text-ivory">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-stone2">{s.body}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-6 py-20 md:py-28">
        <Eyebrow>Preguntas frecuentes</Eyebrow>
        <div className="mt-10 space-y-4">
          {FAQ.map((item, i) => (
            <FadeIn key={item.q} delay={i * 0.05}>
              <details className="group rounded-xl border border-gold/20 bg-ink2/70 p-5 open:border-gold/40">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-ivory" data-testid={`visitas-faq-${i}`}>
                  <span className="flex items-center gap-3">
                    <MessageCircleQuestion size={17} className="text-gold" /> {item.q}
                  </span>
                  <span className="text-gold transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-stone2">{item.a}</p>
              </details>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* RESERVA */}
      <section id="reservar" className="border-t border-gold/10 bg-ink2/40">
        <div className="mx-auto max-w-3xl px-6 py-20 md:py-28">
          <Eyebrow>Reserva</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl uppercase tracking-wide text-ivory md:text-5xl">
            Reserva tu <span className="text-gold-metallic">visita</span>
          </h2>
          <p className="mt-5 max-w-xl text-stone2">
            Elige día y hora y te confirmamos al momento por WhatsApp. Sin esperas, sin comerciales.
          </p>

          {sent ? (
            <div className="mt-10 rounded-2xl border border-wa/30 bg-wa/10 p-8 text-center" data-testid="visitas-success-panel">
              <CheckCircle2 className="mx-auto text-wa" size={44} />
              <h3 className="mt-4 font-serif text-2xl text-ivory">¡Reserva preparada!</h3>
              <p className="mt-2 text-sm text-stone2">Se abrió WhatsApp con tu solicitud. Si no se abrió, pulsa abajo.</p>
              <a
                href={waUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="visitas-reopen-whatsapp-button"
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3 font-semibold text-ink"
              >
                <WhatsAppIcon className="h-5 w-5" /> Abrir WhatsApp {PHONE_DISPLAY}
              </a>
            </div>
          ) : (
            <FadeIn delay={0.1}>
              <form onSubmit={submit} className="mt-10 space-y-5 rounded-2xl border border-gold/20 bg-ink2/80 p-6 md:p-8" data-testid="visitas-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className={labelCls}>Nombre y apellidos *</label>
                    <input required value={f.name} onChange={set("name")} placeholder="María García López" data-testid="visitas-input-name" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Teléfono *</label>
                    <input required type="tel" value={f.phone} onChange={set("phone")} placeholder="600 000 000" data-testid="visitas-input-phone" className={inputCls} />
                  </div>
                  <div>
                    <label className={labelCls}>Inmueble o referencia</label>
                    <input value={f.property_ref} onChange={set("property_ref")} placeholder="Ej.: Ref. TH-000101 o calle…" data-testid="visitas-input-ref" className={inputCls} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Fecha</label>
                      <input type="date" value={f.date} onChange={set("date")} data-testid="visitas-input-date" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Hora</label>
                      <input type="time" value={f.time} onChange={set("time")} data-testid="visitas-input-time" className={inputCls} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className={labelCls}>¿Algo más que debamos saber?</label>
                  <textarea rows={3} value={f.notes} onChange={set("notes")} placeholder="Opcional…" data-testid="visitas-input-notes" className={inputCls} />
                </div>
                {err && <p className="text-sm text-red-400" data-testid="visitas-error">{err}</p>}
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="visitas-submit-button"
                  className="flex w-full items-center justify-center gap-3 rounded-full bg-wa py-4 text-sm font-bold uppercase tracking-[0.2em] text-ink transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <WhatsAppIcon className="h-5 w-5" />}
                  {sending ? "Enviando…" : "Reservar por WhatsApp"}
                </button>
              </form>
            </FadeIn>
          )}

          <div className="mt-8 rounded-xl border border-gold/20 bg-ink2/70 p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-subtle">Enlace directo de esta página</p>
            <p className="mt-2 text-sm text-stone2">Compártelo con quien quiera visitar una vivienda a su aire:</p>
            <button
              onClick={copyLink}
              data-testid="copy-visitas-link-button"
              className="mt-3 flex w-full items-center gap-2 rounded-lg border border-gold/30 px-4 py-2.5 text-left text-sm text-gold transition-colors hover:bg-gold/10 sm:w-fit"
            >
              {copied ? <Check size={15} className="shrink-0" /> : <Copy size={15} className="shrink-0" />}
              <span className="break-all">{copied ? "¡Enlace copiado!" : `${window.location.origin}/visitas`}</span>
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Visitas;
